import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, Users, Euro, Clock, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [courses, setCourses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [bookingsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/api/bookings.php?action=my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/courses.php?action=list`)
      ]);
      
      setBookings(bookingsRes.data);
      
      // Create courses lookup
      const coursesMap = {};
      coursesRes.data.forEach(course => {
        coursesMap[course.id] = course;
      });
      setCourses(coursesMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-900/30 text-green-400 border border-green-700/30">Bezahlt</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-900/30 text-yellow-400 border border-yellow-700/30">Ausstehend</Badge>;
      default:
        return <Badge className="bg-gray-900/30 text-gray-400 border border-gray-700/30">{status}</Badge>;
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-16 px-6" data-testid="dashboard-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-12 animate-fadeIn">
            <p className="text-xs uppercase tracking-widest text-primary mb-4">
              Mein Bereich
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-2">
              Hallo, <span className="text-primary">{user.name}</span>
            </h1>
            <p className="text-muted-foreground">
              Verwalten Sie hier Ihre Kursbuchungen
            </p>
          </div>

          {/* Bookings */}
          <div className="animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <h2 className="text-2xl font-heading font-semibold mb-6">
              Meine Buchungen
            </h2>

            {loading ? (
              <div className="text-center py-16">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border rounded-md">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Sie haben noch keine Kurse gebucht.</p>
                <Button onClick={() => navigate('/courses')} className="btn-primary">
                  Kurse entdecken
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map(booking => {
                  const course = courses[booking.course_id];
                  if (!course) return null;

                  return (
                    <div 
                      key={booking.id} 
                      className="bg-card border border-border rounded-md p-6 card-hover"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading font-semibold text-lg">
                              {course.name}
                            </h3>
                            {getStatusBadge(booking.payment_status)}
                            {booking.booking_type === 'couple' && (
                              <Badge className="bg-primary/20 text-primary border border-primary/30">
                                <Users className="w-3 h-3 mr-1" />
                                Paar
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(course.start_date).toLocaleDateString('de-DE')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {course.schedule}
                            </span>
                            <span className="flex items-center gap-1">
                              <Euro className="w-4 h-4" />
                              €{booking.booking_type === 'couple' ? (course.price * 2).toFixed(2) : course.price.toFixed(2)}
                            </span>
                          </div>

                          {booking.partner_email && (
                            <p className="mt-2 text-sm text-muted-foreground">
                              Partner: {booking.partner_name || booking.partner_email}
                            </p>
                          )}
                        </div>

                        <Button 
                          variant="outline" 
                          onClick={() => navigate(`/courses/${course.id}`)}
                          className="gap-2"
                        >
                          Details
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
